import {
  DebtAprAsset,
  makePercent,
  Percent,
  PositionReward,
} from "@silo/types";
import { Array, BigInt, pipe } from "effect";
import { byAprDesc } from "./rewards";

export type DebtRewardsState = "none" | "netCost" | "netYield";

export interface DebtAprMetrics {
  totalDebtRewardsApr: Percent;
  hasDebtRewards: boolean;
  totalDebtApr: Percent;
  debtRewards: PositionReward[];
  debtRewardsState: DebtRewardsState;
  showFixedLabel: boolean;
}

export function getDebtAprMetrics(
  asset: Pick<
    DebtAprAsset,
    "debtBaseApr" | "debtRewardsApr" | "debtMerklRewards" | "isDebtAprFixed"
  >,
  nativeRewardsSymbol: string
): DebtAprMetrics {
  const { debtBaseApr, debtRewardsApr, debtMerklRewards, isDebtAprFixed } =
    asset;

  const totalDebtRewardsApr = pipe(
    [debtRewardsApr, ...debtMerklRewards.map((reward) => reward.apr)],
    BigInt.sumAll,
    makePercent
  );

  const hasDebtRewards = totalDebtRewardsApr > 0n;

  const totalDebtApr = pipe(debtBaseApr - totalDebtRewardsApr, makePercent);

  const nativeReward: PositionReward = {
    rewardTokenSymbol: nativeRewardsSymbol,
    apr: debtRewardsApr,
    isSuperFest: false,
  };

  const debtRewards: PositionReward[] = pipe(
    [nativeReward, ...debtMerklRewards],
    Array.filter((reward) => reward.apr > 0n),
    Array.sort(byAprDesc)
  );

  const debtRewardsState = getDebtRewardsState(hasDebtRewards, totalDebtApr);
  const showFixedLabel = isDebtAprFixed && debtRewardsState === "none";

  return {
    totalDebtRewardsApr,
    hasDebtRewards,
    totalDebtApr,
    debtRewards,
    debtRewardsState,
    showFixedLabel,
  };
}

function getDebtRewardsState(
  hasDebtRewards: boolean,
  totalDebtApr: Percent
): DebtRewardsState {
  if (!hasDebtRewards) return "none";

  return totalDebtApr >= 0n ? "netCost" : "netYield";
}
